import { Badge, Button, MonoLabel, StatusPill, costValueColor } from '@/design-system';
import { NHG_CLASSIFICATION_LABELS } from '@/entities/finding';
import { REPORT_COST_KEYS, type ReportCostKey, type ReportFinding } from '@/entities/report';
import { formatCurrency, formatDayTimeShort } from '@/shared/lib';

import { reportCopy } from '../copy';
import { SurveyJumpButton } from './SurveyJump';

export interface CostFindingCardProps {
    finding: ReportFinding;
    /** Mag deze persoon het geld van deze bevinding nu aanpassen? */
    canAdjust: boolean;
    /** Is deze persoon de inspecteur van de opname? */
    canConfirm: boolean;
    busy: boolean;
    onAdjust: (finding: ReportFinding) => void;
    onConfirm: (finding: ReportFinding) => void;
}

const LABELS: Record<ReportCostKey, string> = {
    direct: reportCopy.costs.columnDirect,
    termijn: reportCopy.costs.columnTerm,
    onderhoud: reportCopy.costs.columnImprovement,
};

/** 12,5 blijft 12,5; 3 blijft 3. Hoogstens drie decimalen, zoals de server bewaart. */
function formatQuantity(quantity: number): string {
    return new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 3 }).format(quantity);
}

/**
 * Hoe het bedrag ontstond, in één regel: "12,5 × € 260 = € 3.250" bij een vraag
 * naar een hoeveelheid, of wat voor bedrag het anders is. Een eenheidsprijs van
 * € 1 betekent dat de inspecteur het bedrag zelf intikte (`docs/nhg.md` §2.2).
 */
function calculationLines(finding: ReportFinding): string[] {
    const copy = reportCopy.costs;

    if (finding.manual) {
        return [copy.calculationManual];
    }

    const unit = finding.unitCosts;
    const calculated = finding.calculatedCosts;

    if (finding.quantity === null || unit === null || calculated === null) {
        return [copy.calculationFixed];
    }

    const priced = REPORT_COST_KEYS.filter((key) => unit[key] > 0);

    if (priced.length > 0 && priced.every((key) => unit[key] === 1)) {
        return [copy.calculationEntered];
    }

    return priced.map(
        (key) =>
            `${LABELS[key]}: ${copy.calculationQuantity(
                formatQuantity(finding.quantity ?? 1),
                formatCurrency(unit[key]),
                formatCurrency(calculated[key]),
            )}`,
    );
}

/**
 * Eén bevinding achter een regel van de NHG-bijlage: waar ze staat, wat ze kost en
 * hoe dat bedrag ontstond, en — als iemand het aanpaste — wie, wanneer, waarom en of
 * de inspecteur akkoord is. Het oorspronkelijke bedrag blijft doorgestreept zichtbaar
 * naast het bedrag dat telt.
 */
export function CostFindingCard({
    finding,
    canAdjust,
    canConfirm,
    busy,
    onAdjust,
    onConfirm,
}: CostFindingCardProps) {
    const copy = reportCopy.costs;
    const calculated = finding.calculatedCosts;
    const shown = REPORT_COST_KEYS.filter(
        (key) => finding.costs[key] !== 0 || (calculated !== null && calculated[key] !== 0),
    );
    const adjustment = finding.adjustment;
    const nhgCode = finding.nhg?.row ?? finding.nhgCode;

    return (
        <article
            aria-label={finding.finding}
            className="grid gap-[10px] rounded-[9px] border border-[var(--border-default)] bg-[var(--surface-card)] p-[14px]"
        >
            <div className="flex flex-wrap items-start justify-between gap-[10px]">
                <div className="min-w-0 flex-[1_1_240px]">
                    <h6 className="m-0 font-[family-name:var(--font-body)] text-[14px] leading-[1.35] font-medium text-[var(--text-primary)]">
                        {finding.finding}
                    </h6>
                    <p className="m-0 mt-[3px] text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                        {[
                            finding.section,
                            finding.location ? copy.location(finding.location) : null,
                            nhgCode
                                ? copy.nhgLine(nhgCode, finding.nhg?.element ?? null) +
                                  (finding.ownNhgCode ? ` (${copy.nhgOwnCode})` : '')
                                : null,
                        ]
                            .filter(Boolean)
                            .join(' · ')}
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-[6px]">
                    {finding.manual ? (
                        <Badge variant="text" tone="neutral">
                            {copy.manual}
                        </Badge>
                    ) : null}
                    {finding.costsAdjusted || adjustment ? (
                        <Badge variant="text" tone="accent">
                            {copy.adjusted}
                        </Badge>
                    ) : null}
                </div>
            </div>

            {finding.explanation ? (
                <p className="m-0 line-clamp-3 text-[13px] leading-[1.55] text-[var(--text-reading)]">
                    {finding.explanation}
                </p>
            ) : null}

            {shown.length > 0 ? (
                <dl className="m-0 grid gap-[10px] [grid-template-columns:repeat(auto-fit,minmax(118px,1fr))]">
                    {shown.map((key) => {
                        const was = calculated?.[key] ?? null;
                        const changed = was !== null && was !== finding.costs[key];

                        return (
                            <div
                                key={key}
                                className="rounded-[7px] bg-[var(--surface-subtle)] px-[10px] py-2"
                            >
                                <dt className="text-[12px] text-[var(--text-tertiary-safe)]">
                                    {LABELS[key]}
                                </dt>
                                <dd className="m-0 mt-[2px] flex flex-wrap items-baseline gap-x-2">
                                    <span
                                        className="font-[family-name:var(--font-mono)] text-[14px] font-medium"
                                        style={{ color: costValueColor[key] }}
                                    >
                                        {formatCurrency(finding.costs[key])}
                                    </span>
                                    {changed ? (
                                        <span className="font-[family-name:var(--font-mono)] text-[12px] text-[var(--text-tertiary-safe)]">
                                            <s aria-hidden="true">{formatCurrency(was)}</s>
                                            <span className="sr-only">
                                                {copy.calculatedWas(formatCurrency(was))}
                                            </span>
                                        </span>
                                    ) : null}
                                </dd>
                            </div>
                        );
                    })}
                </dl>
            ) : null}

            <div className="grid gap-[3px]">
                {calculationLines(finding).map((line) => (
                    <MonoLabel
                        key={line}
                        size={11}
                        variant="value"
                        tone="muted"
                        as="p"
                        className="m-0"
                    >
                        {line}
                    </MonoLabel>
                ))}
                {finding.nhgClassification ? (
                    <p className="m-0 text-[12px] text-[var(--text-secondary)]">
                        {copy.nhgChoice(NHG_CLASSIFICATION_LABELS[finding.nhgClassification])}
                    </p>
                ) : null}
            </div>

            {adjustment ? (
                <div className="flex flex-wrap items-center gap-x-[10px] gap-y-[6px] border-t border-[var(--border-subtle)] pt-[10px]">
                    <p className="m-0 min-w-0 flex-[1_1_260px] text-[12px] leading-[1.5] text-[var(--text-secondary)]">
                        {copy.adjustedBy(
                            adjustment.byName,
                            adjustment.at ? formatDayTimeShort(adjustment.at) : null,
                        )}
                        {adjustment.reason ? `: ${adjustment.reason}` : ''}
                    </p>
                    {adjustment.awaitsInspector ? (
                        <StatusPill tone="warn" dot>
                            {copy.awaitsInspector}
                        </StatusPill>
                    ) : adjustment.byInspector ? (
                        <StatusPill tone="neutral">{copy.byInspector}</StatusPill>
                    ) : (
                        <StatusPill tone="accent" dot>
                            {copy.confirmedBy(adjustment.confirmedByName)}
                        </StatusPill>
                    )}
                </div>
            ) : null}

            <div className="flex flex-wrap items-center justify-end gap-2 empty:hidden">
                {/* Het bedrag komt uit een antwoord; daar pas je de bron aan. */}
                <SurveyJumpButton
                    sectionPath={finding.sectionPath}
                    what={finding.finding}
                    size="md"
                />
                {canConfirm && adjustment?.awaitsInspector ? (
                    <Button
                        variant="primary"
                        size="md"
                        disabled={busy}
                        aria-label={copy.confirmLabel(finding.finding)}
                        onClick={() => onConfirm(finding)}
                    >
                        {copy.confirm}
                    </Button>
                ) : null}
                {canAdjust ? (
                    <Button
                        variant="outline"
                        size="md"
                        icon="edit"
                        disabled={busy}
                        aria-label={copy.adjustLabel(finding.finding)}
                        onClick={() => onAdjust(finding)}
                    >
                        {copy.adjust}
                    </Button>
                ) : null}
            </div>
        </article>
    );
}
